import { baseURLV2 } from "../../axios";
import { convertToEnglishDigit } from "../../helpers/Functions";
import ApiService from "./ApiService";

class OrderApiService extends ApiService {
  /**
   *
   * @param {number} id
   * @param {number} page
   * @returns {Promise}
   */
  async getOrders(id, page = 1, status, date, table_id) {
    const params = {
      status,
      date,
      table_id,
    };

    try {
      const res = await this.axios.get(`providers/${id}/orders?page=${page}`, {
        params,
      });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @param {number} orderID
   * @param {string} status
   * @param {string} processingTime
   * @returns {Promise} Promise
   */
  async changeOrderStatus(providerID, orderID, status, processingTime) {
    const formData = new FormData();

    const providerDetails = { processingTime };

    formData.append("status", status);

    formData.append(
      "provider_details",
      JSON.stringify(JSON.stringify(providerDetails))
    );

    try {
      const res = await this.axios.post(
        `providers/${providerID}/orders/${orderID}/review`,
        formData
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} orderId
   * @param {number} providerID
   * @param {string} userPhoneNumber
   * @param {string} price
   * @param {[]} _items
   * @param {string} description
   * @param {string} deskNumber
   * @param {string} tableId
   * @param {string} address
   * @param {string} orderType
   * @param {string} deliveryFee
   * @param {string} status order status => pending - approved - denied
   * @param {string} paymentType online - offline
   * @param {string} processingTime
   * @param {boolean} isIntentionalChange
   * @returns {Promise}
   */
  async createOrEditOrder(
    orderId = null,
    providerID,
    userPhoneNumber,
    price,
    discount,
    _items,
    description,
    deskNumber,
    tableId,
    address,
    orderType,
    deliveryFee,
    status,
    paymentType,
    processingTime,
    isIntentionalChange,
    sendPrintNotification
  ) {
    let items;

    if (_items) {
      items = {};
      for (const itemIndex in _items) {
        const item = _items[itemIndex];

        items[+itemIndex + 1] = {
          id: item.product.id,
          title: item.product.title,
          price: item.product.price,
          quantity: item.unit,
          discount: item.product.discount,
          level: item.product.categoryLevel,
          categoryId: item.product.categoryId,
        };
      }
    }

    const formData = new FormData();

    const userDetails = {};
    if (orderType === "indoor") {
      if (tableId) formData.append("table_id", tableId);
      if (deskNumber)
        Object.assign(userDetails, {
          destination: deskNumber,
        });
    } else {
      Object.assign(userDetails, {
        destination: address,
      });
    }

    if (description) {
      Object.assign(userDetails, {
        description,
      });
    }

    if (deliveryFee) {
      const fee = convertToEnglishDigit(
        deliveryFee.toString().splitNumberFormat()
      );
      formData.append("delivery_fee", fee);

      Object.assign(userDetails, {
        delivery: { fee },
      });
    }

    if (orderType === "outdoor" && processingTime) {
      const providerDetails = { processingTime };
      formData.append(
        "provider_details",
        JSON.stringify(JSON.stringify(providerDetails))
      );
    }

    formData.append("price", +convertToEnglishDigit(price));
    formData.append("type", orderType);

    formData.append(
      "user_details",
      JSON.stringify(JSON.stringify(userDetails))
    );
    if (items) formData.append("items", JSON.stringify(JSON.stringify(items)));
    if (discount) formData.append("discount", discount.toString());
    formData.append("payment_type", paymentType);

    if (userPhoneNumber) {
      formData.append("phone", convertToEnglishDigit(userPhoneNumber));
    }

    let url = `providers/${providerID}/orders`;

    if (orderId) {
      url = `providers/${providerID}/orders/${orderId}`;
      formData.append("_method", "PATCH");

      /**
       * It's for letting the server know if something is going to change or not
       */
      if (isIntentionalChange)
        formData.append(
          "is_intentional_change",
          isIntentionalChange ? "1" : "0"
        );

      if (sendPrintNotification) {
        formData.append("status", status);
      }
    } else {
      /**
       * Only need to send status when creating order
       */
      formData.append("status", status);
    }

    try {
      const res = await this.axios.post(url, formData);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerId
   * @param {number} orderId
   * @returns {Promise}
   */
  async sendOrderLink(providerId, orderId) {
    try {
      const res = await this.axios.post(
        `providers/${providerId}/orders/${orderId}/send`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {string} phone
   * @returns {Promise}
   */
  async searchUser(phone) {
    try {
      const res = await this.axios.get(
        `users/search?phone=${convertToEnglishDigit(phone)}`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} id
   * @param {number} page
   * @returns {Promise}
   */
  async getOrderReports(id, page = 1) {
    try {
      const res = await this.axios.get(`providers/${id}/payments?page=${page}`);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} id
   * @param {number[]} orderIds
   * @returns {Promise}
   */
  async deleteOrderReceipt(id, orderId) {
    try {
      const res = await this.axios.delete(`providers/${id}/orders/${orderId}`);
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerId
   * @param {number} orderId
   * @returns {Promise}
   */
  async settlePayment(providerId, orderId) {
    try {
      const res = await this.axios.post(
        `providers/${providerId}/orders/${orderId}/pay`,
        {},
        { baseURL: baseURLV2 }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }
  async generateReceiptNumber(providerId, orderId) {
    try {
      const res = await this.axios.post(
        `providers/${providerId}/orders/${orderId}/generate_receipt_number`,
        {},
        { baseURL: baseURLV2 }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }
}

const orderApiService = new OrderApiService();
export default orderApiService;
